"use client";
import { IBaseLabelValue } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import { cn, convertToJalaliDateTime } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import ResultContent from "@/components/resultContent";
import React, { useRef } from "react";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";

import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedCarDocumentResData } from "@/interfaces/inquiries/vehicle";

const CarDocumentInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCarDocumentResData;
  navigateToLogin: () => void;
}) => {
  console.log(data);
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const carDocumentData = data.data.data;
  const concatPlate = `${data.data.input.plk_serial} - ${data.data.input.plk3}${data.data.input.plk2}${data.data.input.plk1}`;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("carDocumentInquiry.nationalCode"),
      value: data.data.input.national_code,
    },
    {
      label: t("carDocumentInquiry.plateNo"),
      value: concatPlate,
    },
  ];

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("carDocumentInquiry.cardStatus"),
      value: carDocumentData.cardStatus.description,
    },
    {
      label: t("carDocumentInquiry.cardPostalBarcode"),
      value: carDocumentData.cardPostalBarcode || "-",
    },
    {
      label: t("carDocumentInquiry.cardIssuanceDate"),
      value: carDocumentData.cardIssuanceDate
        ? convertToJalaliDateTime(carDocumentData.cardIssuanceDate)
        : "-",
    },
    {
      label: t("carDocumentInquiry.cardPrintDate"),
      value: carDocumentData.cardPrintDate
        ? convertToJalaliDateTime(carDocumentData.cardPrintDate)
        : "-",
    },
    {
      label: t("carDocumentInquiry.cardType"),
      value: carDocumentData.cardType.description,
    },
    {
      label: t("carDocumentInquiry.documentStatus"),
      value:
        carDocumentData.documentStatus === 1
          ? t("carDocumentInquiry.printed")
          : t("carDocumentInquiry.notPrinted"),
    },
    {
      label: t("carDocumentInquiry.documentPostalBarcode"),
      value: carDocumentData.documentPostalBarcode || "-",
    },
    {
      label: t("carDocumentInquiry.documentIssuanceDate"),
      value: carDocumentData.documentIssuanceDate
        ? convertToJalaliDateTime(carDocumentData.documentIssuanceDate)
        : "-",
    },
    {
      label: t("carDocumentInquiry.documentPrintDate"),
      value: carDocumentData.documentPrintDate
        ? convertToJalaliDateTime(carDocumentData.documentPrintDate)
        : "-",
    },
    {
      label: t("carDocumentInquiry.documentType"),
      value: carDocumentData.documentType.description,
    },
  ];

  const pdfContentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("carDocumentInquiry.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="car-document-download-pdf"
            resultContent={resultContent}
            tableTitle="گزارش استعلام کارت و سند خودرو"
            fileName={`گزارش-استعلام-کارت-و-سند-خودرو-${concatPlate}`}
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>
        <ResultContent content={resultContent} className="md:gap-4" />

        <FlexCenter className="mt-10 flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="car-document-download-pdf"
            useHtmlToPdf={true}
            resultContent={resultContent}
            htmlContentRef={pdfContentRef}
            tableTitle="گزارش استعلام کارت و سند خودرو"
            fileName={`گزارش-استعلام-کارت-و-سند-خودرو-${concatPlate}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-car-document-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-car-document-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Car_Document_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="گزارش استعلام کارت و سند خودرو"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default CarDocumentInquiryDetails;
